import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation, OnChanges, SimpleChanges } from "@angular/core";
import { AgGridModule } from "ag-grid-angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AgGridAngular } from "ag-grid-angular";
import type { GridReadyEvent, GridOptions, GridApi, IGetRowsParams, ColDef } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from "@angular/common";
import { FormsModule } from '@angular/forms';
import { TextboxComponent } from '../../../shared/design-system/textbox/textbox.component';
import { TextboxConfig } from "../../design-system/textbox/textbox.modal";
import { ICONS } from "../../../_helpers/icons.constants";
import { GridConfig } from "../../../_models/common/grid/grid-config";
import { Subscription } from "rxjs";
import { Claims } from "../../../_models/common/claims";
import { ExpenseStatusEnum } from "src/app/_models";
import { NRFCConfigModel } from "src/app/_models/NrFc/nrfcconfig-model";
import { Router } from "@angular/router";
import { CustomHeaderComponent } from '../controls/header-component/custom-header.component';
import { CheckBoxRendererComponent } from '../controls/checkbox-renderer/checkbox-renderer.component';
import { AmountRendererComponent } from "../controls/amount-renderer/amount-renderer.component";
import { ButtonsRendererComponent } from "../controls/buttons-renderer/buttons-renderer.component";
import { ApprovalDataRendererComponent } from "../controls/approval-data-renderer/approval-data-renderer.component";
import { BadgeRendererComponent } from "../controls/badge-renderer/badge-renderer.component";
import { BooleanTextRendererComponent } from "../../grid/controls/boolean-text-renderer/boolean-text-renderer.component";
import { DualButtonFunctionsComponent } from "../controls/dual-button-functions/dual-button-functions.component";
import { TextboxRendererComponent } from "../controls/textbox-renderer/textbox-renderer.component";
import { ToggleRendererComponent } from "../controls/toggle-renderer/toggle-renderer.component";
import { TextAndDescriptionRendererComponent } from "../controls/text-and-description-renderer/text-and-description-renderer.component";
import { TextboxAndIconRendererComponent } from "../controls/textbox-and-icon-renderer/textbox-and-icon-renderer.component";
import { StatusSupportingTextRendererComponent } from "../controls/status-supporting-text-renderer/status-supporting-text-renderer.component";
import { IconRendererComponent } from "../controls/icon-renderer/icon-renderer.component";
import { RadioRendererComponent } from "../controls/radio-renderer/radio-renderer.component";
import { ProgressBarRendererComponent } from "../controls/progress-bar-renderer/progress-bar-renderer.component";
import { StatusRenderComponent } from "../controls/status-renderer/status-renderer.component";
import { StatusAndActionRendererComponent } from "../controls/status-and-action-renderer/status-and-action-renderer.component";
import { StatusDataRenderComponent } from "../../grid/controls/status-data-renderer/status-data-renderer.component";
import { DateRendererComponent } from "../controls/date-renderer/date-renderer.component";
import { DateTimeRendererComponent } from "../controls/date-time-renderer/date-time-renderer.component";
import { CheckIconRendererComponent } from "../controls/check-icon-renderer/check-icon-renderer.component";
import { CardNumberRendererComponent } from "../controls/card-number-renderer/card-number-renderer.component";
import { DownloadRendererComponent } from "../controls/download-renderer/download-renderer.component";
import { PercentageRendererComponent } from "../controls/percentage-renderer/percentage-renderer.component";
import { DiscountRendererComponent } from "../controls/discount-renderer/discount-renderer.component";
import { LogIconRendererComponent } from "../../grid/controls/log-icon-renderer/log-icon-renderer.component";
import { PolicyAmountRendererComponent } from "../controls/policy-amount-renderer/policy-amount-renderer.component";
import { SafeTextRendererComponent } from "../controls/safe-text-renderer/safe-text-renderer.component";
import { ConditionalTextRendererComponent } from "../controls/conditional-text-renderer/conditional-text-renderer.component";
import { LinkRendererComponent } from "../controls/link-renderer/link-renderer.component";
import { PreApprovalTagRendererComponent } from "../controls/pre-approval-tag-renderer/pre-approval-tag-renderer.component";
import { ClickToSetupRendererComponent } from "../controls/click-to-setup-renderer/click-to-setup-renderer.component";
import { TooltipRenderComponent } from "../controls/tooltip-renderer/tooltip-renderer.component";
import { TextWithTooltipRendererComponent } from "../controls/text-tooltip-renderer/text-tooltip-renderer.component";
import { IconButtonsRendererComponent } from "../controls/icon-buttons-renderer/icon-buttons-renderer.component";
import { NgSelectRendererComponent } from "../controls/ng-select-renderer/ng-select-renderer.component";
import { TextboxTwoDecimalNumberComponent } from "../controls/textbox-two-decimal-number-renderer/textbox-two-decimal-number-renderer.component";
import { CategoryInputRendererComponent } from "../controls/category-input-renderer/category-input-renderer.component";
import { ProjectNameRendererComponent } from "../controls/project-name-renderer/project-name-renderer.component";
import { PerkActionLinksRendererComponent } from "../controls/perk-action-links-renderer/perk-action-links-renderer.component";


ModuleRegistry.registerModules([AllCommunityModule]);

interface IRow {
  make: string;
  model: string;
  price: number;
  electric: boolean;
}
@Component({
  standalone: true,
  imports: [AgGridAngular, TranslateModule, AgGridModule, NgSelectModule, CommonModule, FormsModule, TextboxComponent],
  selector: 'grid',
  templateUrl: './grid.component.html',
  encapsulation: ViewEncapsulation.None,


})
export class GridComponent implements OnInit, OnChanges {
  public ICONS = ICONS;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["dropdownList"] && this.dropdownList && this.dropdownList.length) {
        this.selectedFilter = this.dropdownList[0]?.dropdownName;
    }
    if (changes["allGridData"] && this.gridApi) {
      if (this.gridConfigItems?.useClientSideRowModel) {
        // Client-side model: just hand the grid the new rows (autoHeight sizes them).
        this.gridApi.setGridOption('rowData', this.allGridData || []);
      } else {
        // Infinite model: force the active sort to re-apply to the freshly-loaded data.
        // Without this, sortAllRows() skips re-sorting when the sort column/order is unchanged.
        this.isRefresh = true;
        this.gridApi.setGridOption('datasource', this.getDataSource());
      }
    }
  }
  @Input("columns") columnDefs: any[] = [];
  @Input("data") allGridData: any;
  @Input("gridFilterItems") gridFilterItems: string;
  @Input("gridConfigItems") gridConfigItems: GridConfig;
  @Input("pinnedBottomRowData") pinnedBottomRowData: any;
  @Input("dataFilterItems") dataFilterItems: string;
  @Input("buttonFilterItem") buttonFilterItem: boolean = false;
  @Input("tagTextValue") tagTextValue: string;

  // Hides the toolbar's own filter and search without removing them, for consumers that take
  // the toolbar over — e.g. a bulk-action bar shown while rows are selected. Hidden rather than
  // destroyed on purpose: rebuilding the filter's ng-select loses its displayed selection,
  // since pushing a value back through ngModel does not reliably land on that control.
  @Input() hideFilterAndSearch: boolean = false;
  @Input() filterLabel: string = '';
  @Input() onFilterChangeFromParent?: (item: any) => void;
  @Input() dropdownList: any[] = [];

  @Input() customSearchFn?: (rows: any[], searchText: string) => any[];

  @Output() filterChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() searchTextChanged: EventEmitter<string> = new EventEmitter<string>();

  @Output() onCellClick = new EventEmitter<any>();
  @Output() onCheckAll = new EventEmitter<boolean>();
  @Output() dataFilterChanged = new EventEmitter<any>();

  @ViewChild('searchTotalRow', { read: ElementRef }) searchElement: ElementRef<any>;

  gridWrapperstyle: any = {
    width: '100%',
    height: '100%',
  };
  rowClassRules;
  gridlastRowIndex: number;
  private isAllChecked: boolean = false;
  filters: any = null;
  param: string;
  resizeSubscription$: Subscription
  overlayLoadingTemplate: string;
  overlayNoRowsTemplate: string;
  isDataLoading: boolean = false;
  isRefresh: boolean = false;
  textNotFound: string = this.translate.instant('no_data_available');

  gridData: any;
  gridApi: GridApi;
  gridOptions: GridOptions;
  gridSortColumn: string;
  gridSortOrder: string;
  selectedFilter: any = null;
  selectedPeople: any[] = [];
  searchText: string = '';
  searchTextboxConfig: TextboxConfig = {
    label: "",
    type: "input",
    id: "fun-search",
    placeholder: "Search here",
    size: "md",
    iconSrc: this.ICONS.SEARCH,
    iconPosition: "left",
    value: this.searchText,
    required: false
  };
  currentDropdown = this.dropdownList && this.dropdownList.length ? this.dropdownList[0].dropdownName : null;
  people: any[] = [];

  ExpenseStatusEnum = ExpenseStatusEnum;
  showFilterPanel: boolean = false;
  claims: Claims;
  isContainerList: boolean = false;
  nrfcConfigModel: NRFCConfigModel = new NRFCConfigModel();
  constructor(private translate: TranslateService, private router: Router) {
    this.overlayNoRowsTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative p-0">
        ${this.translate.instant("no_data_availabe")}
      </div>
    `;
    this.overlayLoadingTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative p-0">
        ${this.translate.instant("data_loading")}
      </div>
    `;
    const currentUrl = this.router.url;
    this.isContainerList = currentUrl.includes('expensecontainerlist');
  }

  ngOnInit() {
    if (!this.gridConfigItems?.suppressAllFilterOption
      && this.dropdownList && !this.dropdownList.find(d => d.dropdownName === 'All' || d.filterKey === 'All')) {
      this.dropdownList.unshift({ dropdownName: 'All' });
    }
    if (!this.selectedFilter) {
      this.selectedFilter = this.currentDropdown ?? (this.dropdownList && this.dropdownList.length ? this.dropdownList[0].dropdownName : null);
    }
    this.handleFilterChange(this.selectedFilter);
    this.initializeGrid();
  }

  public refreshGridData() {
    this.overlayNoRowsTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative p-0">
        ${this.translate.instant(this.gridConfigItems.noDataMessage)}
      </div>
    `;
      this.initializeGrid();
      if (this.gridApi) {
          if (this.gridConfigItems?.useClientSideRowModel) {
              this.gridApi.setGridOption('rowData', this.allGridData || []);
          } else {
              // Re-apply the active sort to the (re)loaded data — see ngOnChanges note.
              this.isRefresh = true;
              this.resetDataSource();
          }
      }
      this.checkUncheckAll();
  }

  public setDataLoading(isDataLoading) {
    this.isDataLoading = isDataLoading;
    if (this.gridlastRowIndex > 0) {
      this.isDataLoading = false;
      this.gridApi.hideOverlay();
    }
    else if (this.isDataLoading) {
      this.gridApi.showLoadingOverlay();
    }
    else {
      this.gridApi.showNoRowsOverlay();
    }
  }

  public initializeGrid(isDataLoading: boolean = true) {
    let self = this;
    this.isDataLoading = isDataLoading;
    if (this.columnDefs && this.columnDefs.length > 0) {
      this.fnAddCheckboxColumn(this.columnDefs);

      this.overlayNoRowsTemplate = `
      <div class="text-center noDataAvailable w-100pc f-16 position-relative p-0">
          ${this.translate.instant(this.gridConfigItems.noDataMessage)}
        </div>
      `;

      this.columnDefs.forEach(columnDef => {
        if (!columnDef.headerComponentParams) {
          columnDef.headerComponentParams = {};
        }
        columnDef.headerComponentParams.rowCount = this.allGridData.length;
      });
      const clientSide = !!this.gridConfigItems?.useClientSideRowModel;
      this.gridOptions = {
        rowModelType: clientSide ? 'clientSide' : 'infinite',
        ...(clientSide ? { rowData: this.allGridData || [] } : { cacheBlockSize: 100, maxBlocksInCache: 3 }),
        // Default uniform row height (replaces the old hardcoded [rowHeight] template input).
        // In client-side mode, column autoHeight overrides this per row to fit content.
        rowHeight: 40,
        pagination: false,
        components: {
            cbxRenderer: CheckBoxRendererComponent,
            agColumnHeader: CustomHeaderComponent,
            amountRenderer: AmountRendererComponent,
            buttonsRenderer: ButtonsRendererComponent,
            approvalWithDataRenderer: ApprovalDataRendererComponent,
            BadgeRenderer: BadgeRendererComponent,
            booleanTextRenderer: BooleanTextRendererComponent,
            dualButtonFunctions: DualButtonFunctionsComponent,
            textboxRenderer: TextboxRendererComponent,
            toggleRenderer: ToggleRendererComponent,
            textAndDescriptionRenderer: TextAndDescriptionRendererComponent,
            textboxAndIconRenderer: TextboxAndIconRendererComponent,
            statusSupportingTextRenderer: StatusSupportingTextRendererComponent,
            iconRenderer: IconRendererComponent,
            radioRenderer: RadioRendererComponent,
            progressBarRenderer: ProgressBarRendererComponent,
            statusRenderer: StatusRenderComponent,
            statusDataRender: StatusDataRenderComponent,
            dateRenderer: DateRendererComponent,
            dateTimeRenderer: DateTimeRendererComponent,
            checkboxIconRenderer: CheckIconRendererComponent,
            cardNumberRenderer: CardNumberRendererComponent,
            downloadRenderer:DownloadRendererComponent,
            percentageRenderer: PercentageRendererComponent,
            discountRenderer: DiscountRendererComponent,
            logRenderer: LogIconRendererComponent,
            PolicyAmountRenderer: PolicyAmountRendererComponent,
            safeTextRenderer: SafeTextRendererComponent,
            conditionalTextRenderer: ConditionalTextRendererComponent,
            linkRenderer: LinkRendererComponent,
            preApprovalTagRenderer: PreApprovalTagRendererComponent,
            clickToSetupRenderer: ClickToSetupRendererComponent,
            customTooltip: TooltipRenderComponent,
            textWithTooltipRenderer: TextWithTooltipRendererComponent,
            iconButtonsRenderer: IconButtonsRendererComponent,
            ngSelectRenderer: NgSelectRendererComponent,
            textboxTwoDecimalNumberRenderer: TextboxTwoDecimalNumberComponent,
            categoryInputRenderer: CategoryInputRendererComponent,
            projectNameRenderer: ProjectNameRendererComponent,
            statusAndActionRenderer: StatusAndActionRendererComponent,
            perkActionLinksRenderer: PerkActionLinksRendererComponent,
        },
        defaultColDef: {
          flex: 1,
          sortable: true,
          resizable: true,
        filter: 'agTextColumnFilter',
          // Off by default (uniform rows). A column opts in with `autoHeight: true` to size rows
          // to its content; the global `.ag-cell-wrapper{height:100%}` rule is relaxed for
          // `.ag-cell-auto-height` cells in _ag-grid.scss so the measurement works.
          autoHeight: true
          },
          tooltipShowDelay: 0,
        columnDefs: this.columnDefs,
      };
      if (this.gridApi) {
        this.gridApi.setGridOption('columnDefs', []);
        this.gridApi.setGridOption('columnDefs', this.columnDefs);
        this.gridApi.setGridOption('datasource', this.getDataSource());
      }
    }
  }

  public fnAddCheckboxColumn(columnDefs) {
    let self = this;
    if (self.gridConfigItems?.enableRowSelection) {
      if (!this.columnDefs.find(c => c.field == 'selected')) {
        const cbxColumn = {
          headerName: "",
          field: "selected",
          cellRenderer: 'cbxRenderer',
          sortable: false,
          resizable: false,
          maxWidth: 60,
          pinned: true,
          headerClass: 'text-center',
          headerComponent: 'agColumnHeader',
          headerComponentParams: {
            isCheckbox: true,
            isAllChecked: false,
            isCheckAllAllowed: true,
            onCheckAll: (event) => this.onAllChecked(event.target.checked)
          },
          suppressMovable: true,
          lockPosition: true,
          lockPinned: true,
        };
        this.columnDefs.unshift(cbxColumn);
      }
    }
  }

  onAllChecked(isChecked: boolean) {
    for (let i = 0; i < this.allGridData.length; i++) {
      if (!this.searchText && !this.filters) {
        if (!this.allGridData[i].isEditDisabled && !this.allGridData[i].isCheckboxDisabled) {
          this.allGridData[i]['selected'] = isChecked;
        }
      }
      if (this.gridData?.length > i) {
        if (!this.gridData[i].isEditDisabled && !this.gridData[i].isCheckboxDisabled) {
          this.gridData[i]['selected'] = isChecked;
        }
      }
    }
    this.isAllChecked = isChecked;
    this.refreshCheckbox();
    this.checkUncheckAll();
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
    this.gridApi.redrawRows();
    this.onCheckAll.emit(isChecked);
  }

  checkUncheckAll() {
    if (this.gridApi && this.gridConfigItems?.enableRowSelection) {
      if (this.allGridData && this.allGridData.length > 0) {
        const selectableRows = this.allGridData.filter((row: any) => !row.isEditDisabled && !row.isCheckboxDisabled);
        const checkedCount = selectableRows.filter((row: any) => row.selected).length;
        this.isAllChecked = selectableRows.length > 0 && checkedCount === selectableRows.length;
      } else {
        this.isAllChecked = false;
      }
      this.refreshCheckbox();
    }
  }

  public checkItems(ids: any[]) {
    if (!this.allGridData || !this.gridConfigItems?.rowKey) return;
    const rowKey = this.gridConfigItems.rowKey;
    this.allGridData.forEach(row => {
      if (ids.includes(row[rowKey])) {
        row.selected = true;
      }
    });
    this.checkUncheckAll();
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
      this.gridApi.redrawRows();
    }
  }

  public uncheckAll() {
    this.onAllChecked(false);
  }

  public refreshCheckbox() {
    if (this.gridApi) {
      if (this.gridConfigItems?.enableRowSelection) {
        var cbxColumn = this.columnDefs.find(c => c.field == 'selected');
        if (cbxColumn) {
          cbxColumn.headerComponentParams.isCheckAllAllowed = this.gridConfigItems?.isCheckAllAllowed ?? true;
          cbxColumn.headerComponentParams.isAllChecked = this.isAllChecked;
        }
        this.updateGridColumnDefinitions(null);
      }
    }
  }

  public updateGridColumnDefinitions(columnDefs: ColDef[]) {
    if (this.gridApi) {
      if (columnDefs && columnDefs.length > 0) {
        this.columnDefs = columnDefs;
        this.fnAddCheckboxColumn(columnDefs);
      }

      this.columnDefs.forEach(columnDef => {
        if (!columnDef.headerComponentParams) {
          columnDef.headerComponentParams = {};
        }
        columnDef.headerComponentParams.rowCount = this.allGridData.length;
      });

      //this.gridApi.setColumnDefs([]);
      this.gridApi.setGridOption('columnDefs', []);
      this.gridApi.setGridOption('columnDefs', this.columnDefs);


    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    // Per-row height: honour an explicit `gridRowHeight` if a consumer sets one; otherwise return
    // undefined so the grid decides � column `autoHeight` measures content (e.g. stacked note
    // chips), and rows without an auto-height column fall back to the `rowHeight` option (40).
    this.gridApi.setGridOption('getRowHeight', (p: any) =>
      (p && p.data && p.data.gridRowHeight) ? p.data.gridRowHeight : undefined);
    if (this.gridConfigItems?.useClientSideRowModel) {
      this.gridApi.setGridOption('rowData', this.allGridData || []);
    } else {
      this.gridApi.setGridOption('datasource', this.getDataSource());
    }
    this.resetSortModel();
  }

  private getDataSource() {
    return {
      getRows: (params: IGetRowsParams) => {
        const data = this.allGridData || [];
        let filtered = data;
        if (this.searchText && this.searchText.trim() !== '') {
          const lowerSearch = this.searchText.toLowerCase();
          if (this.customSearchFn) {
            filtered = this.customSearchFn(
              this.allGridData,
              this.searchText
            );
          } else {
            filtered = data.filter(row => Object.keys(row).some(key => {
              const index = row[key];
              return index !== null && index !== undefined && index.toString().toLowerCase().indexOf(lowerSearch) > -1;
            }));
          }

        }

        this.sortAllRows(params);
        if (this.searchText && this.searchText.trim() !== '') {
          const lowerSearch = this.searchText.toLowerCase();
          if (this.customSearchFn) {
            filtered = this.customSearchFn(
              this.allGridData,
              this.searchText
            );
          } else {
            filtered = this.allGridData.filter(row => Object.keys(row).some(key => {
              const index = row[key];
              return index !== null && index !== undefined && index.toString().toLowerCase().indexOf(lowerSearch) > -1;
            }));
          }

        } else {
          filtered = this.allGridData;
        }
        const total = filtered.length;
        const rowsForBlock = filtered.slice(params.startRow, params.endRow);
        params.successCallback(rowsForBlock, total);
        if (total === 0) {
          setTimeout(() => this.gridApi?.showNoRowsOverlay());
        } else {
          this.gridApi?.hideOverlay();
        }
      }
    };
  }

  onCellClicked(params: any) {
    if (params.column.colId === 'selected') {
      if (params.data.isCheckboxDisabled) {
        return;
      }
      params.data.selected = params.event.target.checked;
      this.onCellClick.emit(params);
      this.checkUncheckAll();
      return;
    }
    this.onCellClick.emit(params);
  }

  onGridSearchTextChange(value: string) {
    this.searchText = value ? value.trim() : '';
    if (this.gridConfigItems?.useClientSideRowModel) {
      if (this.gridApi) { this.gridApi.setGridOption('quickFilterText', value || ''); }
    } else {
      this.refreshData();
    }
    this.searchTextChanged.emit(value);
  }

  refreshData() {
    if (this.gridApi) {
      this.isDataLoading = false;
      if (this.gridlastRowIndex > 0) {
        this.gridApi.refreshInfiniteCache();
      }
      else {
        this.resetDataSource();
      }
    }
  }

  resetDataSource() {
    this.gridApi.setGridOption('datasource', this.getDataSource());
  }

  handleFilterChange(item: any) {
    if (this.onFilterChangeFromParent) {
      this.onFilterChangeFromParent(item);
    }
    this.filterChanged.emit(item);
  }

  onClear() {
    const allValue = this.currentDropdown ?? (this.dropdownList && this.dropdownList.length ? this.dropdownList[0].dropdownName : null);
    this.selectedFilter = allValue;
    if (this.onFilterChangeFromParent) {
      this.onFilterChangeFromParent(allValue);
    }
    this.filterChanged.emit(allValue);
  }

  public resetSortModel() {
    if (!this.gridConfigItems) return;
    this.sortGrid(this.gridConfigItems.defaultSortColumn, this.gridConfigItems.defaultSortOrder);
  }

  public setSortModel(sortColumn: string, sortDirection: string) {
    this.sortGrid(sortColumn, sortDirection);
  }

  private sortGrid(sortColumn, sortDirection) {
    this.gridApi.applyColumnState({
      state: [
        {
          colId: sortColumn,
          sort: sortDirection
        }
      ],
      defaultState: { sort: null }
    });
  }

  private sortAllRows(params) {
    if (params.sortModel && params.sortModel.length > 0) {
      if (this.gridSortColumn != params.sortModel[0].colId
        || this.gridSortOrder != params.sortModel[0].sort
        || this.isRefresh) {
        params.startRow = 0;
        params.endRow = this.gridOptions.cacheBlockSize;
        this.gridSortColumn = params.sortModel[0].colId;
        this.gridSortOrder = params.sortModel[0].sort;
        this.allGridData = this.sortByKey(this.allGridData, this.gridSortColumn, this.gridSortOrder);
      }
    }
    else {
      if (!this.gridSortColumn || !this.gridSortOrder || this.isRefresh) {
        params.startRow = 0;
        params.endRow = this.gridOptions.cacheBlockSize;
        this.gridSortColumn = this.gridConfigItems?.defaultSortColumn;
        this.gridSortOrder = this.gridConfigItems?.defaultSortOrder;
        this.allGridData = this.sortByKey(this.allGridData, this.gridSortColumn, this.gridSortOrder);
      }
    }
    this.isRefresh = false;
  }

  private sortByKey(array, key, order) {
    let result;
    if (order == 'asc') {
      result = array.sort((a, b) => {
        let x = a[key]; let y = b[key];
        if (typeof x === 'string' || x instanceof String) {
          x = x ? x.toString().toLowerCase() : null;
          y = y ? y.toString().toLowerCase() : null;
        }
        return !x ? -1 : ((x < y) ? -1 : ((x > y) ? 1 : 0));
      });
    }
    else {
      result = array.sort((a, b) => {
        let x = a[key]; let y = b[key];
        if (typeof x === 'string' || x instanceof String) {
          x = x ? x.toString().toLowerCase() : null;
          y = y ? y.toString().toLowerCase() : null;
        }
        return !y ? -1 : ((x > y) ? -1 : ((x < y) ? 1 : 0));
      });
    }
    return result;
  }
}